import WishListAction from '../wishlist/WishListAction'
import { useEffect, useId, useMemo, useState } from 'react'
import { buildSearchHierarchy, catalogRowIsOwned } from '../../lib/catalogSearchHierarchy'
import { demoteBlankImageItems, lineRepIsPlaceholder, resolveCatalogSearchPresentation, rowHasUsableCardImage } from '../../lib/catalogSearchHouseFirst'
import { resolvedConcentrationDisplay } from '../../lib/concentrationResolver'
import { lineKeySignature, liveLineKey, rowsForLiveLine } from '../../lib/catalogSearchLiveLines'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import CatalogSearchGrouped from '../search/CatalogSearchGrouped'
import { BottleThumbnail } from '../collection/HierarchyMultiAddModal'
import { ConcentrationPill } from './CatalogResultVisuals'

function BottleRow({ row, owned, disabled, onSelect }) {
  return (
    <div>
    <button
      key={row.id}
      type="button"
      disabled={disabled}
      onClick={() => onSelect(row)}
      data-catalog-id={row.id}
      data-in-collection={owned(row)}
      data-search-step="concentration"
      className="flex min-h-16 w-full items-center gap-2 rounded-lg border border-white/10 p-2 text-left hover:bg-gold/5 disabled:opacity-50"
    >
      <BottleThumbnail row={row} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-cream">{row.name}</span>
        <span className="block text-[11px] text-cream/40">{row.year || 'Year unverified'}</span>
        {owned(row) && <span className="block text-[11px] font-semibold text-sage">In your collection</span>}
      </span>
      <ConcentrationPill concentration={resolvedConcentrationDisplay(row)} />
    </button>
    <WishListAction catalogId={row.id} owned={owned(row) || disabled} source="search" />
    </div>
  )
}

function FragranceList({ fragrances, lineKey, panelId, owned, disabled, onSelect, activeFragrance, setActiveFragrance }) {
  return fragrances.map(fragrance => {
    const fragranceKey = `${lineKey}\u0000${fragrance.name}`
    const onlyFragrance = fragrances.length === 1
    const multiConcentration = fragrance.variants.length > 1
    if (!multiConcentration) {
      return (
        <BottleRow
          key={fragrance.variants[0].id}
          row={fragrance.variants[0]}
          owned={owned}
          disabled={disabled}
          onSelect={onSelect}
        />
      )
    }
    const fragranceOpen = onlyFragrance || activeFragrance === fragranceKey
    const fragrancePanelId = `${panelId}-${fragrance.name.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    return (
      <div key={fragranceKey} data-fragrance-entry>
        <button
          type="button"
          aria-expanded={fragranceOpen}
          aria-controls={fragrancePanelId}
          disabled={disabled}
          onClick={() => setActiveFragrance(fragranceOpen && !onlyFragrance ? null : fragranceKey)}
          className="flex min-h-12 w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-white/5 disabled:opacity-50"
          data-fragrance-row
          data-search-step="fragrance"
        >
          <span>
            <span className="block text-sm text-cream">{fragrance.name}</span>
            <span className="text-[11px] text-cream/40">
              {fragrance.variants.length} concentrations
            </span>
          </span>
          <span aria-hidden="true" className="text-gold">{fragranceOpen ? '−' : '+'}</span>
        </button>
        {fragranceOpen && (
          <div
            id={fragrancePanelId}
            role="group"
            aria-label={`${fragrance.name} concentrations`}
            className="space-y-1 pl-2"
          >
            {fragrance.variants.map(row => (
              <BottleRow
                key={row.id}
                row={row}
                owned={owned}
                disabled={disabled}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    )
  })
}

// FOS-SEARCH-LINE-GROUPING-001: every catalog search surface (Home wear
// search, Fast Add, Daily Use Add, Wish List) renders through this component,
// so the founder flag is read here once. Off (the default, and whenever the
// flag row is missing) renders the line hierarchy below, untouched. On renders
// one row per fragrance name with its concentrations as sub-rows.
export default function CatalogSearchHierarchy(props) {
  const nameGrouped = useFeatureFlag('name_grouped_search')
  if (nameGrouped) return <CatalogSearchGrouped {...props} />
  return <CatalogSearchLineHierarchy {...props} />
}

// Search uses the same display hierarchy and bottle visuals as house browse.
// Path is house → line → fragrance → concentration. Catalog identity of the
// selected source row remains unchanged.
function CatalogSearchLineHierarchy({
  query = '',
  rows,
  lines = [],
  loadLineVariants,
  collection = [],
  findDuplicate,
  onSelect,
  disabled = false,
}) {
  const presentation = useMemo(
    () => resolveCatalogSearchPresentation(query, rows, lines),
    [query, rows, lines],
  )
  const hasLiveLines = presentation.useLiveLines
  const liveSignature = useMemo(() => lineKeySignature(presentation.displayLines), [presentation.displayLines])
  const houses = presentation.houses
  const [activeLine, setActiveLine] = useState(null)
  const [activeFragrance, setActiveFragrance] = useState(null)
  const [liveVariants, setLiveVariants] = useState([])
  const [liveLoading, setLiveLoading] = useState(false)
  const instance = useId()
  const owned = row => collection.length
    ? catalogRowIsOwned(row, collection)
    : Boolean(findDuplicate?.({ ...row, concentration: resolvedConcentrationDisplay(row) }))

  useEffect(() => {
    setActiveLine(null)
    setActiveFragrance(null)
    setLiveVariants([])
  }, [rows, lines, query])

  useEffect(() => {
    if (!hasLiveLines || !activeLine) {
      setLiveVariants([])
      setLiveLoading(false)
      return undefined
    }
    const line = presentation.displayLines.find(entry => liveLineKey(entry) === activeLine)
    if (!line) return undefined
    let cancelled = false
    setLiveLoading(true)
    const apply = (nextRows) => {
      if (cancelled) return
      setLiveVariants(Array.isArray(nextRows) ? nextRows : [])
      setLiveLoading(false)
    }
    if (typeof loadLineVariants === 'function') {
      Promise.resolve(loadLineVariants(line.house, line.line_key))
        .then((nextRows) => apply(nextRows?.length ? nextRows : rowsForLiveLine(rows, line)))
        .catch(() => apply(rowsForLiveLine(rows, line)))
    } else {
      apply(rowsForLiveLine(rows, line))
    }
    return () => { cancelled = true }
  }, [activeLine, hasLiveLines, liveSignature, loadLineVariants, rows, presentation.displayLines])

  return (
    <div
      data-catalog-search-hierarchy
      data-search-path="house-line-fragrance-concentration"
      data-search-lead={presentation.lead}
      data-matched-house={presentation.house || undefined}
      data-search-line-source={hasLiveLines ? 'fos_search_lines_v1' : 'derived'}
    >
      {houses.map(house => (
        <section key={house.house} aria-label={house.house} data-search-step="house">
          <h3 className="px-3 py-2 text-xs uppercase tracking-wider text-gold">{house.house}</h3>
          {house.lines.map((line, index) => {
            const key = hasLiveLines ? liveLineKey(line) : `${house.house}\u0000${line.name}`
            const expanded = activeLine === key
            const panelId = `${instance}-${house.house}-${index}`
            const label = hasLiveLines ? (line.line_label || line.line_key) : line.name
            const variantRows = hasLiveLines
              ? (expanded ? liveVariants : [])
              : line.variants
            const ownedCount = variantRows.filter(owned).length
            const variantCount = hasLiveLines
              ? Number(line.variant_count) || variantRows.length
              : line.variants.length
            const thumbRow = hasLiveLines
              ? {
                  id: line.rep_id,
                  name: label,
                  house: line.house,
                  card_image: line.rep_card_image,
                  image_flagged: line.image_flagged,
                }
              : (line.variants.find(rowHasUsableCardImage) || line.variants[0])
            const fragrances = demoteBlankImageItems(
              hasLiveLines
                ? buildSearchHierarchy(demoteBlankImageItems(variantRows)).flatMap(entry => (
                  entry.lines.flatMap(item => item.fragrances.map(fragrance => ({
                    ...fragrance,
                    variants: demoteBlankImageItems(fragrance.variants),
                  })))
                ))
                : (line.fragrances || []).map(fragrance => ({
                  ...fragrance,
                  variants: demoteBlankImageItems(fragrance.variants),
                })),
              fragrance => (fragrance.variants || []).find(rowHasUsableCardImage) || fragrance.variants?.[0],
            )
            return (
              <div key={key} className="border-b border-white/5" data-line-entry>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  disabled={disabled}
                  onClick={() => {
                    setActiveLine(expanded ? null : key)
                    setActiveFragrance(null)
                  }}
                  className="flex min-h-16 w-full items-center gap-3 px-3 py-2 text-left hover:bg-white/5 focus:bg-white/5 disabled:opacity-50"
                  data-line-row
                  data-search-step="line"
                  data-line-key={hasLiveLines ? line.line_key : undefined}
                  data-line-placeholder={
                    (hasLiveLines ? lineRepIsPlaceholder(line) : !rowHasUsableCardImage(thumbRow))
                      ? 'true'
                      : 'false'
                  }
                >
                  <BottleThumbnail row={thumbRow} size="line" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-cream">{label}</span>
                    <span className="text-[11px] text-cream/40">
                      {variantCount} {variantCount === 1 ? 'bottle' : 'bottles'}
                      {ownedCount ? ` · ${ownedCount} owned` : ''}
                    </span>
                  </span>
                  <span aria-hidden="true" className="text-gold">{expanded ? '−' : '+'}</span>
                </button>
                {expanded && (
                  <div
                    id={panelId}
                    role="group"
                    aria-label={`${label} fragrances`}
                    className="space-y-1 px-2 pb-2"
                    data-search-step="fragrance-list"
                  >
                    {hasLiveLines && liveLoading && (
                      <p className="px-2 py-2 text-xs text-cream/45">Loading bottles in this line…</p>
                    )}
                    {!liveLoading && (
                      <FragranceList
                        fragrances={fragrances}
                        lineKey={key}
                        panelId={panelId}
                        owned={owned}
                        disabled={disabled}
                        onSelect={onSelect}
                        activeFragrance={activeFragrance}
                        setActiveFragrance={setActiveFragrance}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </section>
      ))}
    </div>
  )
}
